import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';
import { AgentLanding } from './routes/agent/AgentLanding';
import { AgentRolePlay } from './routes/agent/RolePlay';
import { HRAgent } from './routes/agent/HR';
import { OnboardingAgent } from './routes/agent/Onboarding';
import { PSAgent } from './routes/agent/ps';
import LeadsList from './routes/agent/LeadsList';
import ColdCall from './routes/agent/ColdCall';
import CustomerSupport from './routes/agent/cs';
import CustomerExperience from './routes/agent/cx';
import ComplianceTraining from './routes/agent/compliance';
import CoachCategories from './routes/coach/coach-categories';
import CoachChat from './routes/coach/coach-chat';
import CoachLanding from './routes/coach/coach-landing';
import { CoachLayout } from './routes/coach/coach-layout';
import { CoachRolePlay } from './routes/coach/coach-roleplay';
import { CoachRoleplayFeedback } from './routes/coach/coach-roleplay-feedback';
import { CoachRolePlayWebRTC } from './routes/coach/coach-roleplay-webrtc';
import { CoachRolePlay11 } from './routes/coach/coach-roleplay11';
import { CustomRolePlay } from './routes/custom/RolePlay';
import SompoDemo from './routes/custom/sompo';
import { InsuranceRoleplay } from './routes/roleplay/InsuranceRoleplay';
import { JapaneseInsuranceRoleplay } from './routes/roleplay-jp/JapaneseInsuranceRoleplay';
import { CXRoleplayWrapper } from './routes/cx/CXRoleplayWrapper';
import { CXAssessmentWrapper } from './routes/cx/CXAssessmentWrapper';
import CustomerCategories from './routes/customer/categores';
import CustomerChat from './routes/customer/chat';
import CustomerLanding from './routes/customer/landing';
import { CustomerRolePlay } from './routes/customer/roleplay';
import { MeetingFeedback } from './routes/meeting/MeetingFeedback';
import MeetingLanding from './routes/meeting/MeetingLanding';
import { MeetingList } from './routes/meeting/MeetingList';
import MeetingCallAnalyzer from './routes/meetingcall/MeetingCallAnalyzer';
import MeetingCallResults from './routes/meetingcall/MeetingCallResults';
import { AppHomepage } from './routes/app/AppHomepage';
import { AppTextChat } from './routes/app/AppTextChat';
import { AppVoiceCall } from './routes/app/AppVoiceCall';
import { AppVideoCall } from './routes/app/AppVideoCall';
import { AppLayout } from './routes/app/components/AppLayout';
import { AppPastSessions } from './routes/app/AppPastSessions';
import { AppAdmin } from './routes/app/AppAdmin';
import { AppAdminUsers } from './routes/app/AppAdminUsers';
import { AppAdminLayout } from './routes/app/components/AppAdminLayout';
import { RoleplayFeedback } from './routes/role-play/session-feedback/roleplay-feedback';
import SalesCategories from './routes/sales/categories';
import SalesChat from './routes/sales/chat';
import SalesLanding from './routes/sales/landing';
import { SalesRolePlay } from './routes/sales/roleplay';
import { ChatHistory } from './routes/sessions/chat-history';
import { PastSessions } from './routes/sessions/past';
import SlackLanding from './routes/slack';
import SlackPrivacyPolicy from './routes/slack/privacy';
import { VoiceRoleplay } from './routes/slack/role-play/voice-roleplay';
import { VideoRoleplay } from './routes/video/VideoRoleplay';
import SimpleVoice from './routes/testing/SimpleVoice';
import { BOILanding } from './routes/boi/BOILanding';
import { BOIVideoCall } from './routes/boi/BOIVideoCall';
import { BOIVoiceCall } from './routes/boi/BOIVoiceCall';
import { AvatarLanding } from './routes/avatar/AvatarLanding';
import { AvatarVideoCall } from './routes/avatar/AvatarVideoCall';
import { AvatarVoiceCall } from './routes/avatar/AvatarVoiceCall';
import { ScriptedAvatarCallWrapper } from './routes/avatar/ScriptedAvatarCall';
import StaticDemo from './routes/static/StaticDemo';
import DavyLanding from './routes/davy/index';
import AiakoLanding from './routes/aiako/index';
import { SUPPORTED_LANGUAGES } from './utils/constants';

const supportedLanguages = SUPPORTED_LANGUAGES.map(l => l.code);
const defaultLanguage = 'en';

// Language redirect loader with query params preservation
const languageRedirect = ({ request }) => {
  // Get browser language
  const browserLang = navigator.language.split('-')[0];
  const targetLang = supportedLanguages.includes(browserLang)
    ? browserLang
    : defaultLanguage;

  // Get current URL and preserve query parameters
  const url = new URL(request.url);
  const currentPath = url.pathname.replace(/^\/coach/, ''); // Remove /coach prefix
  const queryParams = url.search; // This includes the ? character if there are query params

  // Construct new URL with language prefix and query params
  const newPath = `/${targetLang}/coach${currentPath}${queryParams}`;

  return redirect(newPath);
};

// Validate language parameter loader
const validateLanguage = ({ params, request }) => {
  const { lang } = params;
  if (!supportedLanguages.includes(lang)) {
    // Preserve query parameters when redirecting to default language
    const url = new URL(request.url);
    const currentPath = url.pathname.replace(/^\/[^/]+/, ''); // Remove language prefix
    const queryParams = url.search;

    return redirect(`/${defaultLanguage}${currentPath}${queryParams}`);
  }
  return null;
};

export const router = createBrowserRouter([
  {
    path: '/',
    children: [{ index: true, loader: () => redirect('/coach') }],
  },
  {
    path: ':lang',
    loader: validateLanguage,
    children: [
      {
        path: 'sessions',
        children: [
          {
            path: 'past',
            element: <PastSessions />,
            errorElement: <div>Something went wrong</div>,
          },
          {
            path: ':sessionId/chat',
            element: <ChatHistory />,
            errorElement: <div>Something went wrong</div>,
          },
        ],
      },
      {
        path: 'coach',
        element: <CoachLayout />,
        children: [
          {
            index: true,
            element: <CoachLanding />,
          },
          {
            path: 'categories',
            element: <CoachCategories />,
          },
          {
            path: 'chat',
            element: <CoachChat />,
          },
          {
            path: 'roleplay-webrtc',
            element: <CoachRolePlayWebRTC />,
          },
          {
            path: 'roleplay',
            element: <CoachRolePlay />,
          },
          {
            path: 'roleplay11',
            element: <CoachRolePlay11 />,
          },
          {
            path: 'roleplay-feedback', // for web
            element: <CoachRoleplayFeedback />,
          },
        ],
      },
      {
        path: 'roleplay', // for slack
        element: <VoiceRoleplay />,
      },
      {
        path: 'session-feedback', // for slack
        element: <RoleplayFeedback />,
      },
    ],
  },
  {
    path: '/coach/*',
    loader: languageRedirect,
  },
  {
    path: '/sales',
    children: [
      {
        index: true,
        element: <SalesLanding />,
      },
      {
        path: 'categories',
        element: <SalesCategories />,
      },
      {
        path: 'chat',
        element: <SalesChat />,
      },
      {
        path: 'roleplay',
        element: <SalesRolePlay />,
      },
    ],
  },
  {
    path: '/customer',
    children: [
      {
        index: true,
        element: <CustomerLanding />,
      },
      {
        path: 'categories',
        element: <CustomerCategories />,
      },
      {
        path: 'chat',
        element: <CustomerChat />,
      },
      {
        path: 'roleplay',
        element: <CustomerRolePlay />,
      },
    ],
  },
  {
    path: '/custom',
    children: [
      {
        index: true,
        element: <Navigate to="/" />,
      },
      {
        path: 'roleplay',
        element: <CustomRolePlay />,
      },
      {
        path: 'feedback',
        element: <RoleplayFeedback />,
      },
      {
        path: 'sompo',
        element: <SompoDemo />,
      },
    ],
  },
  // Davy Select FAQ Demo
  {
    path: '/davy',
    element: <DavyLanding />,
  },
  // Aiako FAQ Demo
  {
    path: '/aiako',
    element: <AiakoLanding />,
  },
  // Insurance Practice (Generic for multiple clients)
  {
    path: '/roleplay',
    element: <InsuranceRoleplay />,
  },
  // Japanese Insurance Practice (Haruka Life)
  {
    path: '/roleplay-jp',
    element: <JapaneseInsuranceRoleplay />,
  },
  {
    path: '/video',
    children: [
      {
        index: true,
        element: <Navigate to="/" />,
      },
      {
        path: 'roleplay',
        element: <VideoRoleplay />,
      },
      {
        path: 'feedback',
        element: <RoleplayFeedback />,
      },
    ],
  },
  {
    path: '/agent',
    children: [
      {
        index: true,
        element: <AgentLanding />,
      },
      {
        path: 'roleplay',
        element: <AgentRolePlay />,
      },
      {
        path: 'hr',
        element: <HRAgent />,
      },
      {
        path: 'onboarding',
        element: <OnboardingAgent />,
      },
      {
        path: 'leads',
        element: <LeadsList />,
      },
      {
        path: 'call',
        element: <ColdCall />,
      },
      {
        path: 'cs',
        element: <CustomerSupport />,
      },
      {
        path: 'cx',
        element: <CustomerExperience />,
      },
      {
        path: 'ps',
        element: <PSAgent />,
      },
      {
        path: 'compliance',
        element: <ComplianceTraining />,
      },
    ],
  },
  {
    path: '/slack',
    children: [
      {
        index: true,
        element: <SlackLanding />,
      },
      {
        path: 'privacy',
        element: <SlackPrivacyPolicy />,
      },
    ],
  },
  {
    path: '/meeting',
    children: [
      {
        index: true,
        element: <MeetingLanding />,
      },
      {
        path: 'list',
        element: <MeetingList />,
      },
      {
        path: 'feedback/:meetingId',
        element: <MeetingFeedback />,
      },
    ],
  },
  // Add the new meetingcall route
  {
    path: '/meetingcall',
    children: [
      {
        index: true,
        element: <MeetingCallAnalyzer />,
      },
      {
        path: 'results/:analysisId',
        element: <MeetingCallResults />,
      },
    ],
  },
  // Add the new app route
  {
    path: '/app',
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <AppHomepage />,
      },
      {
        path: 'chat',
        element: <AppTextChat />,
      },
      {
        path: 'voice',
        element: <AppVoiceCall />,
      },
      {
        path: 'video',
        element: <AppVideoCall />,
      },
      {
        path: 'past-sessions',
        element: <AppPastSessions />,
      },
    ],
  },
  // Add separate admin routes without nesting
  {
    path: '/app/admin',
    element: <AppAdminLayout />,
    children: [
      {
        index: true,
        element: <AppAdmin />,
      },
      {
        path: 'users',
        element: <AppAdminUsers />,
      },
    ],
  },
  // Dynamic CX route for all companies (grab, miso, etc.)
  {
    path: '/cx/:companyCode',
    children: [
      {
        path: 'roleplay',
        element: <CXRoleplayWrapper />,
      },
      {
        path: 'roleplay/:sessionId/assessment',
        element: <CXAssessmentWrapper />,
      },
    ],
  },
  // Testing routes
  {
    path: '/testing',
    children: [
      {
        path: 'custom',
        element: <SimpleVoice />,
      },
    ],
  },
  // Bank of Ireland Financial Coach Demo (Legacy - redirects to /avatar?company=boi)
  {
    path: '/boi',
    children: [
      {
        index: true,
        loader: () => redirect('/avatar?company=boi'),
      },
      {
        path: 'video',
        loader: () => redirect('/avatar/video?company=boi'),
      },
      {
        path: 'voice',
        loader: () => redirect('/avatar/voice?company=boi'),
      },
    ],
  },
  // Standard Chartered Bank Wealth Advisory Training (redirects to /avatar?company=scb)
  {
    path: '/scb',
    children: [
      {
        index: true,
        loader: () => redirect('/avatar?company=scb'),
      },
      {
        path: 'video',
        loader: () => redirect('/avatar/video?company=scb'),
      },
      {
        path: 'voice',
        loader: () => redirect('/avatar/voice?company=scb'),
      },
    ],
  },
  // Unified Avatar Demo (supports multiple companies via ?company= query parameter)
  {
    path: '/avatar',
    children: [
      {
        index: true,
        element: <AvatarLanding />,
      },
      {
        path: 'video',
        element: <AvatarVideoCall />,
      },
      {
        path: 'voice',
        element: <AvatarVoiceCall />,
      },
      {
        path: 'scripted',
        element: <ScriptedAvatarCallWrapper />,
      },
    ],
  },
  // Static Demos (supports multiple companies via ?company= query parameter)
  // Usage: /static?company=aia, /static?company=sompo, etc.
  {
    path: '/static',
    element: <StaticDemo />,
  },
]);

// Helper function to get current language from URL
export const getLanguageFromURL = () => {
  const path = window.location.pathname;
  const langMatch = path.match(/^\/([a-z]{2})\//);
  return langMatch ? langMatch[1] : defaultLanguage;
};

// Helper function to construct URL with language prefix
export const constructLanguageUrl = (
  lang: string,
  path: string,
  queryParams: string = '',
) => {
  // Remove any existing language prefix and leading/trailing slashes
  const cleanPath = path.replace(/^\/[a-z]{2}\//, '').replace(/^\/+|\/+$/g, '');
  return `/${lang}/${cleanPath}${queryParams}`;
};

export const supportedLanguagesList = supportedLanguages;
export const defaultLanguageCode = defaultLanguage;
